/**
 * ContentEditorPage: the admin block-list authoring UI.
 *
 * P5 wave 3a (admin content editor). Route: /admin/content/:pageKey,
 * tenant-only, gated by the shared `EcosystemAdminRoute` (`admin`
 * EcosystemMembership in the current tenant; #262 removed apex content
 * editing). `:pageKey` is one of landing / about / mission / contact.
 *
 * Flow:
 *   - On load: GET /api/ecosystem-content/:pageKey/draft -> draftBlocks.
 *   - The blocks are held in local state as an ordered list. Each block has
 *     an inline edit form (blockEditorRegistry); blocks reorder via up/down
 *     controls; blocks add via a type picker; blocks delete with a confirm.
 *   - Save draft: PUT .../draft with the re-sequenced block list. The public
 *     page does NOT change on save.
 *   - Publish: POST .../publish, promoting the draft to live, behind a
 *     confirm. Publish operates on the server's stored draft, so it requires
 *     no unsaved local edits: the UI nudges the admin to save first.
 *   - Live preview: the P5-2 <EcosystemContentRenderer/> renders the current
 *     in-memory draft in a toggleable pane (reused, not reimplemented).
 *
 * `order` integrity: every mutation (add / delete / move) re-sequences the
 * list to a contiguous 0..n-1 `order` via `resequence`, and the saved payload
 * is re-sequenced again defensively, so the renderer's `order` sort is always
 * correct.
 *
 * Unsaved-changes guard: a `beforeunload` listener warns on tab close / hard
 * refresh while `dirty` is true. In-app route changes are not blocked: the
 * app uses a plain BrowserRouter (no data router), so React Router's blocker
 * API is unavailable; the saved/unsaved indicator and the beforeunload guard
 * are the practical safeguards.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Button,
    Spinner,
    Alert,
    Badge,
    Modal,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useContentApi } from '@/shared/services/contentApi';

import EcosystemContentRenderer from '../EcosystemContentRenderer';
import '../Content.css';

import BlockEditorCard from './BlockEditorCard';
import BlockTypePicker from './BlockTypePicker';
import { createBlock, ensureBlockClientIds } from './blockTypes';
import { CONTENT_TEMPLATES, getTemplateBlocks } from './contentTemplates';
import './ContentEditor.css';

// Human-readable titles for the authorable pages.
const PAGE_LABELS = {
    landing: 'Landing Page',
    about: 'About Page',
    mission: 'Mission Page',
    contact: 'Contact Page',
};

const VALID_PAGE_KEYS = Object.keys(PAGE_LABELS);

/**
 * Return a new array with each block's `order` set to its index (0..n-1).
 * Keeps the renderer's `order` sort correct after any add / delete / move.
 *
 * @param {Array} blocks - The working block list.
 * @returns {Array} A re-sequenced copy.
 */
const resequence = (blocks) =>
    blocks.map((block, index) => ({ ...block, order: index }));

export default function ContentEditorPage() {
    const { pageKey } = useParams();
    const contentApi = useContentApi();

    // The working draft block list (always kept order-contiguous).
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // dirty = local edits not yet saved to the server draft.
    const [dirty, setDirty] = useState(false);

    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null); // {variant,text}

    const [publishedAt, setPublishedAt] = useState(null);

    const [showPicker, setShowPicker] = useState(false);
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    // Set of _clientIds whose cards are collapsed. Empty = all expanded.
    const [collapsedIds, setCollapsedIds] = useState(new Set());

    // When the admin uses the template on a page that already has blocks,
    // hold the template id here until the replace-confirm modal is accepted.
    const [pendingTemplateId, setPendingTemplateId] = useState(null);

    const isValidPage = VALID_PAGE_KEYS.includes(pageKey);
    const pageLabel = PAGE_LABELS[pageKey] || pageKey;

    // --- Load the draft on mount / page change -----------------------------
    useEffect(() => {
        if (!isValidPage) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError('');

        const load = async () => {
            try {
                const data = await contentApi.fetchDraftContent(pageKey);
                if (cancelled) {
                    return;
                }
                // Sort the loaded draft by `order` then re-sequence so the
                // working list starts contiguous. Mint stable `_clientId`s
                // for the loaded blocks: the server never persists
                // `_clientId`, so loaded blocks always need one before they
                // can be safely keyed by it (review finding 6).
                const ordered = [...(data.draftBlocks || [])].sort(
                    (a, b) => (a.order ?? 0) - (b.order ?? 0)
                );
                const loaded = ensureBlockClientIds(resequence(ordered));
                setBlocks(loaded);
                setCollapsedIds(new Set(loaded.map((b) => b._clientId)));
                setPublishedAt(data.publishedAt || null);
                setDirty(false);
            } catch {
                if (!cancelled) {
                    setLoadError(
                        'Failed to load the draft for this page. Please try again.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [contentApi, pageKey, isValidPage]);

    // --- Warn on tab close / hard refresh with unsaved edits ---------------
    useEffect(() => {
        if (!dirty) {
            return undefined;
        }
        const handler = (e) => {
            e.preventDefault();
            // Required for the native prompt in some browsers.
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // --- Block mutations ---------------------------------------------------
    // Every mutation marks the editor dirty and keeps `order` contiguous.

    const mutateBlocks = useCallback((updater) => {
        setBlocks((prev) => resequence(updater(prev)));
        setDirty(true);
        setStatusMessage(null);
    }, []);

    const handleChangeData = useCallback(
        (index, nextData) => {
            mutateBlocks((prev) =>
                prev.map((block, i) =>
                    i === index ? { ...block, data: nextData } : block
                )
            );
        },
        [mutateBlocks]
    );

    const handleMoveUp = useCallback(
        (index) => {
            if (index <= 0) {
                return;
            }
            mutateBlocks((prev) => {
                const next = [...prev];
                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                return next;
            });
        },
        [mutateBlocks]
    );

    const handleMoveDown = useCallback(
        (index) => {
            mutateBlocks((prev) => {
                if (index >= prev.length - 1) {
                    return prev;
                }
                const next = [...prev];
                [next[index], next[index + 1]] = [next[index + 1], next[index]];
                return next;
            });
        },
        [mutateBlocks]
    );

    const handleDelete = useCallback(
        (index) => {
            const clientId = blocks[index]?._clientId;
            if (clientId) {
                setCollapsedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(clientId);
                    return next;
                });
            }
            mutateBlocks((prev) => prev.filter((_, i) => i !== index));
        },
        [mutateBlocks, blocks]
    );

    const handleToggleCollapsed = useCallback((clientId) => {
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (next.has(clientId)) {
                next.delete(clientId);
            } else {
                next.add(clientId);
            }
            return next;
        });
    }, []);

    const handleCollapseAll = useCallback(() => {
        setCollapsedIds(new Set(blocks.map((b) => b._clientId)));
    }, [blocks]);

    const handleExpandAll = useCallback(() => {
        setCollapsedIds(new Set());
    }, []);

    const handleAddBlock = useCallback(
        (type) => {
            setShowPicker(false);
            mutateBlocks((prev) => {
                const newBlock = createBlock(type, prev.length);
                if (!newBlock) return prev;
                // Ensure the new block opens expanded.
                setCollapsedIds((ids) => {
                    const next = new Set(ids);
                    next.delete(newBlock._clientId);
                    return next;
                });
                return [...prev, newBlock];
            });
        },
        [mutateBlocks]
    );

    // --- Starter templates -------------------------------------------------

    /**
     * Seed the draft with a template's blocks. `getTemplateBlocks` deep-clones
     * the template definition and re-sequences `order` to 0..n-1; `mutateBlocks`
     * re-sequences again defensively and marks the draft dirty. The blocks
     * fully replace whatever is currently in the draft.
     */
    const applyTemplate = useCallback(
        (templateId) => {
            const templateBlocks = getTemplateBlocks(templateId);
            if (templateBlocks.length === 0) {
                return;
            }
            mutateBlocks(() => templateBlocks);
        },
        [mutateBlocks]
    );

    // Apply the single template for this page. On an empty page it seeds
    // blocks directly; on a non-empty page it defers to the replace-confirm.
    const handleUseTemplate = useCallback(() => {
        const template = CONTENT_TEMPLATES.find((t) => t.pageKey === pageKey);
        if (!template) return;
        if (blocks.length === 0) {
            applyTemplate(template.id);
        } else {
            setPendingTemplateId(template.id);
        }
    }, [blocks.length, applyTemplate, pageKey]);

    // The admin confirmed replacing the current blocks with the chosen template.
    const handleConfirmReplaceTemplate = useCallback(() => {
        if (pendingTemplateId) {
            applyTemplate(pendingTemplateId);
        }
        setPendingTemplateId(null);
    }, [pendingTemplateId, applyTemplate]);

    // --- Save / publish ----------------------------------------------------

    const handleSave = useCallback(async () => {
        setSaving(true);
        setStatusMessage(null);
        try {
            // Re-sequence defensively before sending so the persisted draft
            // is always order-contiguous. `_clientId` is a client-only
            // React key (review finding 6) and is stripped from the wire
            // payload so the server schema does not receive an unknown
            // field: local `blocks` state keeps the ids.
            const resequenced = resequence(blocks);
            const payload = resequenced.map(
                // eslint-disable-next-line no-unused-vars
                ({ _clientId, ...rest }) => rest
            );
            await contentApi.saveDraftContent(pageKey, payload);
            setBlocks(resequenced);
            setDirty(false);
            setStatusMessage({
                variant: 'success',
                text: 'Draft saved. It is not live until you publish.',
            });
        } catch {
            setStatusMessage({
                variant: 'danger',
                text: 'Failed to save the draft. Your changes are still here. Please try again.',
            });
        } finally {
            setSaving(false);
        }
    }, [blocks, contentApi, pageKey]);

    const handlePublish = useCallback(async () => {
        setShowPublishConfirm(false);
        setPublishing(true);
        setStatusMessage(null);
        try {
            const data = await contentApi.publishContent(pageKey);
            setPublishedAt(data.publishedAt || new Date().toISOString());
            setStatusMessage({
                variant: 'success',
                text: 'Published. The live page now shows this content.',
            });
        } catch (err) {
            // The server returns 400 "Nothing to publish" when the stored
            // draft is empty: surface that distinctly.
            const serverMessage = err?.response?.data?.message;
            setStatusMessage({
                variant: 'danger',
                text: serverMessage || 'Failed to publish. Please try again.',
            });
        } finally {
            setPublishing(false);
        }
    }, [contentApi, pageKey]);

    // The renderer skips unknown / partial blocks itself; pass the working
    // list straight through for the live preview.
    const previewBlocks = useMemo(() => blocks, [blocks]);

    // --- Render ------------------------------------------------------------

    if (!isValidPage) {
        return (
            <Container className="py-5">
                <Alert variant="warning">
                    <Alert.Heading>Unknown page</Alert.Heading>
                    <p className="mb-0">
                        &quot;{pageKey}&quot; is not an editable page. Valid
                        pages are: {VALID_PAGE_KEYS.join(', ')}.
                    </p>
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Edit {pageLabel} | RILE Connect</title>
            </Helmet>
            <Container className="py-4 content-editor">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                    <div>
                        <h1 className="text-primary mb-1">Edit {pageLabel}</h1>
                        <p className="text-muted mb-0">
                            Compose this page from content blocks. Saving stores
                            a draft; publishing makes the draft live.
                        </p>
                    </div>
                    <div className="text-end">
                        {dirty ? (
                            <Badge bg="warning" text="dark">
                                Unsaved changes
                            </Badge>
                        ) : (
                            <Badge bg="success">All changes saved</Badge>
                        )}
                        <div className="small text-muted mt-1">
                            {publishedAt
                                ? `Last published ${new Date(
                                      publishedAt
                                  ).toLocaleString()}`
                                : 'Not yet published'}
                        </div>
                    </div>
                </div>

                {loadError && <Alert variant="danger">{loadError}</Alert>}
                {statusMessage && (
                    <Alert
                        variant={statusMessage.variant}
                        dismissible
                        onClose={() => setStatusMessage(null)}
                    >
                        {statusMessage.text}
                    </Alert>
                )}

                {/* Action bar */}
                <div className="d-flex flex-wrap gap-2 mb-4">
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || publishing || !dirty}
                    >
                        {saving ? 'Saving…' : 'Save Draft'}
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => setShowPublishConfirm(true)}
                        disabled={saving || publishing}
                    >
                        {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowPicker(true)}
                        disabled={saving || publishing}
                    >
                        + Add Block
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={handleUseTemplate}
                        disabled={saving || publishing}
                    >
                        Use Template
                    </Button>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowPreview((v) => !v)}
                    >
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {blocks.length > 0 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                onClick={handleCollapseAll}
                            >
                                Collapse All
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={handleExpandAll}
                            >
                                Expand All
                            </Button>
                        </>
                    )}
                </div>

                <Row>
                    {/* Block list */}
                    <Col lg={showPreview ? 6 : 12}>
                        <h2 className="h5 text-muted mb-3">Blocks</h2>
                        {blocks.length === 0 ? (
                            <Alert variant="light" className="border">
                                <Alert.Heading className="h6">
                                    This page has no blocks yet
                                </Alert.Heading>
                                <p>
                                    Get a running start: apply the starter
                                    template (a pre-arranged layout with
                                    placeholder content you can replace) or
                                    build the page block by block.
                                </p>
                                <div className="d-flex flex-wrap gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleUseTemplate}
                                        disabled={saving || publishing}
                                    >
                                        Use Template
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setShowPicker(true)}
                                        disabled={saving || publishing}
                                    >
                                        + Add Block
                                    </Button>
                                </div>
                            </Alert>
                        ) : (
                            blocks.map((block, index) => (
                                <BlockEditorCard
                                    key={block._clientId}
                                    block={block}
                                    index={index}
                                    isFirst={index === 0}
                                    isLast={index === blocks.length - 1}
                                    collapsed={collapsedIds.has(
                                        block._clientId
                                    )}
                                    onToggleCollapsed={() =>
                                        handleToggleCollapsed(block._clientId)
                                    }
                                    onChangeData={(nextData) =>
                                        handleChangeData(index, nextData)
                                    }
                                    onMoveUp={() => handleMoveUp(index)}
                                    onMoveDown={() => handleMoveDown(index)}
                                    onDelete={() => handleDelete(index)}
                                />
                            ))
                        )}
                    </Col>

                    {/* Live preview */}
                    {showPreview && (
                        <Col lg={6}>
                            <h2 className="h5 text-muted mb-3">Live Preview</h2>
                            <div className="content-editor-preview border rounded">
                                {previewBlocks.length === 0 ? (
                                    <p className="text-muted p-4 mb-0">
                                        Nothing to preview yet.
                                    </p>
                                ) : (
                                    <EcosystemContentRenderer
                                        blocks={previewBlocks}
                                    />
                                )}
                            </div>
                            <p className="small text-muted mt-2">
                                This preview reflects your unsaved working
                                draft.
                            </p>
                        </Col>
                    )}
                </Row>
            </Container>

            <BlockTypePicker
                show={showPicker}
                onSelect={handleAddBlock}
                onClose={() => setShowPicker(false)}
            />

            {/* Replace-confirm: only shown when a template is chosen for a page
                that already has blocks. Applying it discards the current
                blocks. */}
            <Modal
                show={pendingTemplateId !== null}
                onHide={() => setPendingTemplateId(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Replace the current blocks?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Applying this template replaces all blocks currently on
                        this page with the template&apos;s blocks. This affects
                        your working draft only: nothing is published until you
                        publish. This cannot be undone from the editor.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setPendingTemplateId(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmReplaceTemplate}
                    >
                        Replace blocks
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showPublishConfirm}
                onHide={() => setShowPublishConfirm(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Publish {pageLabel}?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Publishing makes the saved draft the live page that
                        every visitor sees.
                    </p>
                    {dirty && (
                        <Alert variant="warning" className="mb-0">
                            You have unsaved changes. Publishing uses the{' '}
                            <strong>last saved</strong> draft: your current
                            unsaved edits will NOT be published. Save first if
                            you want them included.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowPublishConfirm(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handlePublish}>
                        Publish now
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
